import { Col, Divider, Row, Typography } from 'antd';
import { useFormikContext } from 'formik';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { type CompanyFormValues, defaultSocialMediaValue } from '.';
import * as permissionKind from '../../../../../shared/permissions';
import { useAccount } from '../../../../components/contexts/AccountContextManager';
import { useCompany } from '../../../../components/contexts/CompanyContextManager';
import SingleUploadField from '../../../../components/fields/SingleUploadField';
import SocialMediaUrlFieldArray from '../../../../components/fields/SocialMediaUrlFieldArray';
import TranslatedTextAreaField from '../../../../components/fields/TranslatedTextAreaField';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import FormFields from '../../../../themes/admin/Fields/FormFields';
import { allowedExtensions } from '../../../../utilities/extensions';
import hasPermissions from '../../../../utilities/hasPermissions';
import { StyledExtraConfigurationTableHeader } from '../../../shared/AdminSharedUI';
import useCompanyDetailsTooltip from './useCompanyDetailsTooltip';

const { Title } = Typography;

const CompanyEdmEmailFooter = () => {
    const { t } = useTranslation('companyDetails');

    const {
        values: {
            edmEmailFooter: { socialMedia },
        },
        setFieldValue,
    } = useFormikContext<CompanyFormValues>();

    const addSocialMedia = useCallback(() => {
        setFieldValue('edmEmailFooter.socialMedia', [...socialMedia, defaultSocialMediaValue]);
    }, [socialMedia, setFieldValue]);

    const { permissions: accountPermissions } = useAccount();
    const company = useCompany(true);
    const hasPermission = company
        ? hasPermissions(company.permissions, [permissionKind.updateCompany]) &&
          hasPermissions(accountPermissions, [permissionKind.updateCompany])
        : hasPermissions(accountPermissions, [permissionKind.updateCompany]);

    const copyrightTooltip = useCompanyDetailsTooltip('fields.copyright.tooltip');

    return (
        <CollapsibleWrapper defaultActiveKey={t('companyDetails:cardTitles.emailFooter')}>
            <Panel key={t('companyDetails:cardTitles.emailFooter')} header={t('companyDetails:cardTitles.emailFooter')}>
                <Row gutter={14}>
                    <Col lg={8} xs={24}>
                        <TranslatedTextAreaField
                            {...t('companyDetails:fields.emailFooter.footerText', { returnObjects: true })}
                            disabled={!hasPermission}
                            name="edmEmailFooter.connectText"
                        />
                    </Col>

                    <Col lg={8} xs={24}>
                        <TranslatedTextAreaField
                            {...t('companyDetails:fields.emailFooter.copyRight', { returnObjects: true })}
                            disabled={!hasPermission}
                            name="edmEmailFooter.copyRight"
                            tooltip={copyrightTooltip}
                            required
                        />
                    </Col>
                    <Col lg={8} xs={24}>
                        <FormFields.InputField
                            addonBefore="https://"
                            {...t('companyDetails:fields.emailFooter.legalNoticeUrl', { returnObjects: true })}
                            disabled={!hasPermission}
                            name="edmEmailFooter.legalNoticeUrl"
                        />
                    </Col>
                    <Col lg={8} xs={24}>
                        <FormFields.InputField
                            addonBefore="https://"
                            {...t('companyDetails:fields.emailFooter.privacyPolicyUrl', { returnObjects: true })}
                            disabled={!hasPermission}
                            name="edmEmailFooter.privacyPolicyUrl"
                        />
                    </Col>
                    <Col lg={8} xs={24}>
                        <TranslatedTextAreaField
                            {...t('companyDetails:fields.emailFooter.disclaimerText', { returnObjects: true })}
                            disabled={!hasPermission}
                            name="edmEmailFooter.disclaimerText"
                        />
                    </Col>
                    <Col lg={8} xs={24}>
                        <SingleUploadField
                            extensions={allowedExtensions.image}
                            {...t('companyDetails:fields.emailFooter.phoneIcon', { returnObjects: true })}
                            disabled={!hasPermission}
                            name="edmEmailFooter.emailIcon"
                        />
                    </Col>
                    <Col lg={8} xs={24}>
                        <SingleUploadField
                            extensions={allowedExtensions.image}
                            {...t('companyDetails:fields.emailFooter.arrowIcon', { returnObjects: true })}
                            disabled={!hasPermission}
                            name="edmEmailFooter.arrowIcon"
                        />
                    </Col>
                    <Col lg={8} xs={24}>
                        <SingleUploadField
                            extensions={allowedExtensions.image}
                            {...t('companyDetails:fields.emailFooter.emailIcon', { returnObjects: true })}
                            disabled={!hasPermission}
                            name="edmEmailFooter.phoneIcon"
                        />
                    </Col>
                </Row>

                <Divider />
                <StyledExtraConfigurationTableHeader>
                    <Title level={5} style={{ fontSize: '14px' }}>
                        {t('companyDetails:emailFooter.socialMediaIcons')}
                    </Title>
                </StyledExtraConfigurationTableHeader>

                <SocialMediaUrlFieldArray
                    addSocialMedia={addSocialMedia}
                    disabled={!hasPermission}
                    name="edmEmailFooter.socialMedia"
                />
            </Panel>
        </CollapsibleWrapper>
    );
};

export default CompanyEdmEmailFooter;
